import { FolderOpenIcon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { apiBaseUrl } from '@/lib/api';
import { cn } from '@/lib/utils';

interface ProjectIconProps {
  readonly className?: string;
  readonly displayName: string;
  readonly iconUrl?: string | null | undefined;
  readonly size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES = {
  sm: 'size-6 text-micro',
  md: 'size-8 text-xs',
  lg: 'size-12 text-base',
} as const;

const IMG_SIZE_CLASSES = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-8',
} as const;

const FALLBACK_ICON_CLASSES = {
  sm: 'size-3',
  md: 'size-4',
  lg: 'size-6',
} as const;

function getInitial(name: string): string {
  const trimmed = name.trim();
  const first = trimmed.charAt(0);
  if (first === '') return '?';
  return first.toUpperCase();
}

/**
 * Deterministic color from a string — picks from a set of oklch-friendly
 * hues so each project gets a consistent tint.
 */
function hashHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

/**
 * Displays a project icon: website favicon, or a colored-initial circle fallback.
 *
 * Website targets are routed through the backend favicon endpoint so images pass
 * strict Content-Security-Policy directives on hosted deployments.
 */
export function ProjectIcon({ className, displayName, iconUrl, size = 'md' }: ProjectIconProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const handleError = useCallback(() => { setImgFailed(true); }, []);

  const resolvedUrl = iconUrl && !imgFailed ? resolveIconUrl(iconUrl) : null;

  if (resolvedUrl) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-md bg-muted/60',
          SIZE_CLASSES[size],
          className,
        )}
      >
        <img
          alt=""
          className={cn('rounded-sm object-contain', IMG_SIZE_CLASSES[size])}
          onError={handleError}
          src={resolvedUrl}
        />
      </span>
    );
  }

  // Fallback: colored initial or folder icon
  const hue = hashHue(displayName);
  const initial = getInitial(displayName);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md font-bold',
        SIZE_CLASSES[size],
        className,
      )}
      style={{
        backgroundColor: `oklch(0.92 0.04 ${String(hue)})`,
        color: `oklch(0.45 0.12 ${String(hue)})`,
      }}
    >
      {initial === '?' ? (
        <FolderOpenIcon className={FALLBACK_ICON_CLASSES[size]} />
      ) : (
        initial
      )}
    </span>
  );
}

/** Known image file extensions. The field accepts website targets, not direct image links. */
const IMAGE_PATH_EXT_RE = /\.(png|jpe?g|gif|svg|ico|webp|avif|bmp)$/i;

/** Strict public-domain check: at least one label and a 2+ char TLD. */
const DOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;
const BLOCKED_HOST_SUFFIXES = [
  '.corp',
  '.home',
  '.internal',
  '.invalid',
  '.lan',
  '.local',
  '.localhost',
  '.test',
] as const;

function faviconUrl(target: string): string {
  return `${apiBaseUrl}/icons/favicon?target=${encodeURIComponent(target)}`;
}

function resolveIconUrl(url: string): string | null {
  const trimmed = url.trim();
  if (trimmed.length === 0) return null;

  const parsed = parseWebsiteTarget(trimmed);
  if (parsed === null) return null;
  if (IMAGE_PATH_EXT_RE.test(parsed.pathname)) return null;

  const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '');
  if (!isAcceptedDomain(hostname)) return null;

  return faviconUrl(hostname);
}

function parseWebsiteTarget(target: string): URL | null {
  try {
    if (target.startsWith('http://') || target.startsWith('https://')) {
      return new URL(target);
    }
    if (target.startsWith('//')) {
      return new URL(`https:${target}`);
    }
    return new URL(`https://${target}`);
  } catch {
    return null;
  }
}

function isAcceptedDomain(hostname: string): boolean {
  if (hostname === 'localhost') return false;
  if (BLOCKED_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix))) return false;
  if (!DOMAIN_RE.test(hostname)) return false;
  const labels = hostname.split('.');
  const tld = labels.at(-1);
  return tld !== undefined && !/^\d+$/.test(tld);
}
